/**
 * Pure rules of the /admin/app-logs screen (no Angular, unit tested).
 */

export type AppLogRangePreset = '1h' | '24h' | '7d' | '30d';

const PRESET_SECONDS: Record<AppLogRangePreset, number> = {
  '1h': 3600,
  '24h': 86400,
  '7d': 7 * 86400,
  '30d': 30 * 86400,
};

function pad(value: number): string {
  return value < 10 ? '0' + value : String(value);
}

/** Local `YYYY-MM-DD HH:mm:ss`, the format the backend filters on (server time = Paris). */
export function formatAppLogDateTime(date: Date): string {
  return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate())
    + ' ' + pad(date.getHours()) + ':' + pad(date.getMinutes()) + ':' + pad(date.getSeconds());
}

/** `{from, to}` of a preset, ending now. Unknown presets fall back to 24 h. */
export function appLogPresetRange(preset: string, now: Date): { from: string; to: string } {
  const seconds = PRESET_SECONDS[preset as AppLogRangePreset] ?? PRESET_SECONDS['24h'];
  const from = new Date(now.getTime() - seconds * 1000);
  return { from: formatAppLogDateTime(from), to: formatAppLogDateTime(now) };
}

/**
 * Timeline points `{bucket, level, entries}` into one series per level over the
 * same sorted list of buckets, with 0 where a level had nothing — ApexCharts needs
 * every series to have one value per category.
 */
export function buildAppLogTimeline(
  points: { bucket: string; level: string; entries: number }[],
  levels: string[] = ['error', 'warning', 'info'],
): { categories: string[]; series: { name: string; data: number[] }[] } {
  const categories = Array.from(new Set((points || []).map((p) => p.bucket))).sort();
  const index = new Map<string, number>();
  categories.forEach((bucket, i) => index.set(bucket, i));

  const series = levels.map((level) => {
    const data = new Array(categories.length).fill(0);
    for (const point of points || []) {
      if (point.level === level) {
        data[index.get(point.bucket) as number] += Number(point.entries) || 0;
      }
    }
    return { name: level, data };
  });
  return { categories, series };
}

/** Context as shown in the detail panel: pretty JSON, or a placeholder. */
export function formatAppLogContext(context: unknown): string {
  if (context === null || context === undefined || context === '') {
    return 'No context recorded.';
  }
  if (typeof context === 'string') {
    return context;
  }
  try {
    return JSON.stringify(context, null, 2);
  } catch {
    return String(context);
  }
}

/**
 * Should the Angular error handler report this error to the backend? Not the
 * errors it already absorbs on purpose (ApexCharts teardown, stale lazy chunks —
 * the latter triggers a reload), not the HTTP failures (the backend saw them), and
 * not twice the same message within the throttle window.
 */
export function shouldReportClientError(
  message: string,
  lastReported: Map<string, number>,
  nowMs: number,
  windowMs = 60_000,
): boolean {
  const text = (message || '').trim();
  if (!text) {
    return false;
  }
  if (text.startsWith('Http failure response') || text.includes('HttpErrorResponse')) {
    return false;
  }
  const key = text.slice(0, 200);
  const last = lastReported.get(key);
  if (last !== undefined && nowMs - last < windowMs) {
    return false;
  }
  lastReported.set(key, nowMs);
  return true;
}
